import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from "@/components/ui/accordion";
import { useCountryConfig } from "@/lib/countryConfig";

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQProps {
    faqs?: FAQItem[];
    title?: string;
}

const DEFAULT_FAQS: FAQItem[] = [
  {
        question: "How do I book a helper?",
        answer: "Simply search for the service you need, browse verified helpers in your area, check their profiles and reviews, then tap 'Book Now'. You can book same-day or schedule in advance.",
  },
  {
        question: "Are all helpers background-checked?",
        answer: "Yes. Every helper on our platform undergoes a comprehensive ID verification and background screening before they can accept bookings. We verify national IDs, references, and employment history.",
  },
  {
        question: "What payment methods are accepted?",
        answer: "You pay in the app by MTN Mobile Money, Airtel Money, Visa or Mastercard, after the helper accepts your booking. Cash is not a payment method for jobs booked on Kanfy.",
  },
  {
        question: "What if I'm not satisfied with the service?",
        answer: "We offer a 100% satisfaction guarantee. If you're not happy with the service, contact our support team within 24 hours and we'll arrange a free redo or issue a refund.",
  },
  {
        question: "How quickly can I get a helper?",
        answer: "Many helpers are available same-day. Once you book, the helper confirms within 2 hours on average. For recurring services, you can schedule weekly or monthly bookings.",
  },
  {
        question: "Can I request the same helper again?",
        answer: "Absolutely. You can save your favourite helpers and book them directly. Many families build long-term relationships with their helpers through Kanfy.",
  },
  {
        question: "What service standards do Kanfy helpers follow?",
        answer: "All helpers must meet our Service Standards covering punctuality, communication, safety, and quality of work. View the full standards at our Service Standards page.",
  },
  {
        question: "Where can I find the platform's service standards and code of conduct?",
        answer: "Our Service Standards page outlines what every helper must meet before and during a booking. It covers our full code of conduct, safety guidelines, and quality commitments.",
  },
  {
        question: "What happens if a helper doesn't meet the service standards?",
        answer: "If a helper falls below our standards, you can report them via the booking review. We investigate every report and take action, including suspension or removal from the platform, to protect all clients.",
  },
  ];

export function FAQ({ faqs = DEFAULT_FAQS, title = "Frequently Asked Questions" }: FAQProps) {
    const { config } = useCountryConfig();
    const showHeader = title !== "";
    const items = faqs || DEFAULT_FAQS;

  return (
        <section className="py-10 sm:py-14 bg-[#F8FAFC]">
              <div className="max-w-3xl mx-auto px-4">
                {showHeader && (
                    <div className="text-center mb-8">
                                <h2 className="h-page text-foreground mb-2">{title}</h2>
                                <p className="text-muted-foreground text-sm">Everything you need to know about Kanfy {config.name}</p>
                    </div>
                      )}
                      <Accordion type="single" collapsible className="w-full space-y-2">
                        {items.map((faq, index) => (
                      <AccordionItem
                                      key={index}
                                      value={"item-" + index}
                                      className="border border-gray-100 rounded-2xl overflow-hidden shadow-soft bg-white"
                                    >
                                    <AccordionTrigger className="text-left py-4 px-5 text-sm font-semibold text-foreground hover:no-underline hover:bg-muted transition-colors duration-200 [&>svg]:text-primary">
                                      {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-muted-foreground px-5 pb-5 pt-1 text-sm leading-relaxed border-t border-gray-100">
                                      {faq.answer}
                                    </AccordionContent>
                      </AccordionItem>
                    ))}
                      </Accordion>
              </div>
        </section>
      );
}
